import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle, CreditCard, Building2, Wallet, Globe, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { siteConfig } from '../data/mock';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/forms/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Failed to send message');
      setSubmitted(true);
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-slate-50 via-white to-teal-50">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-20 w-72 h-72 bg-teal-100 rounded-full blur-3xl opacity-40" />
          <div className="absolute bottom-10 left-20 w-64 h-64 bg-amber-100 rounded-full blur-3xl opacity-40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-teal-600 font-semibold text-sm tracking-wide uppercase mb-4 block">Contact Us</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">Get in Touch</h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Have questions about our programs, partnership opportunities, or how you can help? We'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-amber-50/40 via-white to-teal-50/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Contact Information</h2>
                <p className="text-slate-600 mb-8">Reach out to us through any of the channels below. We typically respond within 24-48 hours.</p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Email</h3>
                    <a href={`mailto:${siteConfig.email}`} className="text-teal-600 hover:text-teal-700 transition-colors">
                      {siteConfig.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Phone</h3>
                    <a href={`tel:${siteConfig.phone}`} className="text-teal-600 hover:text-teal-700 transition-colors">
                      {siteConfig.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Mailing Address</h3>
                    <p className="text-slate-600">{siteConfig.address}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Response Time</h3>
                    <p className="text-slate-600">We typically respond within 24-48 hours</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl">Send Us a Message</CardTitle>
                </CardHeader>
                <CardContent>
                  {submitted ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-10 h-10 text-teal-600" />
                      </div>
                      <h3 className="text-2xl font-semibold text-slate-900 mb-3">Message Sent!</h3>
                      <p className="text-slate-600 max-w-md mx-auto">Thank you for reaching out. We'll get back to you within 24-48 hours.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="name">Full Name *</Label>
                          <Input
                            id="name"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            required
                            className="mt-1.5"
                          />
                        </div>
                        <div>
                          <Label htmlFor="email">Email Address *</Label>
                          <Input
                            id="email"
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            required
                            className="mt-1.5"
                          />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="phone">Phone Number</Label>
                          <Input
                            id="phone"
                            type="tel"
                            value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            className="mt-1.5"
                          />
                        </div>
                        <div>
                          <Label htmlFor="subject">Subject *</Label>
                          <Input
                            id="subject"
                            value={form.subject}
                            onChange={(e) => setForm({ ...form, subject: e.target.value })}
                            required
                            placeholder="How can we help?"
                            className="mt-1.5"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="message">Message *</Label>
                        <Textarea
                          id="message"
                          rows={6}
                          value={form.message}
                          onChange={(e) => setForm({ ...form, message: e.target.value })}
                          required
                          placeholder="Please provide details about your inquiry..."
                          className="mt-1.5"
                        />
                      </div>

                      <Button type="submit" size="lg" className="bg-teal-600 hover:bg-teal-700" disabled={submitting} data-testid="contact-submit-btn">
                        {submitting ? 'Sending...' : 'Send Message'}
                        {!submitting && <Send className="w-4 h-4 ml-2" />}
                      </Button>
                      {error && <p className="text-red-500 text-sm mt-2" data-testid="contact-error">{error}</p>}
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Ways to Support Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-amber-400 font-semibold text-sm tracking-wide uppercase">Support the Mission</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 mb-4">Ways to Donate</h2>
            <p className="text-slate-300">Your contribution directly supports our programs and helps transform lives.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-slate-800/50 border-slate-700 text-white">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center mb-4">
                  <CreditCard className="w-6 h-6 text-teal-400" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Check</h3>
                <p className="text-slate-400 text-sm">
                  {siteConfig.donationMethods.check}
                </p>
                <p className="text-slate-500 text-xs mt-2">
                  Mail to: {siteConfig.address}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700 text-white">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center mb-4">
                  <Building2 className="w-6 h-6 text-teal-400" />
                </div>
                <h3 className="font-semibold text-lg mb-2">ACH / Bank Transfer</h3>
                <p className="text-slate-400 text-sm">
                  {siteConfig.donationMethods.bank}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700 text-white">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center mb-4">
                  <Wallet className="w-6 h-6 text-teal-400" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Zelle / PayPal</h3>
                <p className="text-slate-400 text-sm">
                  Contact us for Zelle and PayPal information
                </p>
                <a href={`mailto:${siteConfig.email}`} className="text-teal-400 text-sm hover:text-teal-300 mt-2 inline-block">
                  Request details →
                </a>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700 text-white">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center mb-4">
                  <Globe className="w-6 h-6 text-amber-400" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Online Donations</h3>
                <p className="text-slate-400 text-sm">
                  {siteConfig.donationMethods.online}
                </p>
                <span className="text-amber-400 text-xs mt-2 inline-block">Stay tuned!</span>
              </CardContent>
            </Card>
          </div>

          <div className="mt-8 p-4 bg-slate-800/30 rounded-xl border border-slate-700 flex items-start gap-3 max-w-2xl mx-auto">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-slate-400 text-sm">
              <span className="text-amber-400 font-medium">Note:</span> {siteConfig.donationMethods.note}
            </p>
          </div>
        </div>
      </section>

      {/* FAQ CTA */}
      <section className="py-16 bg-gradient-to-br from-slate-100 to-teal-50/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Have More Questions?</h2>
          <p className="text-slate-600 mb-6">Learn more about our programs, partnerships, and how to get involved.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild variant="outline" className="border-2">
              <Link to="/about">About Us</Link>
            </Button>
            <Button asChild variant="outline" className="border-2">
              <Link to="/programs">Our Programs</Link>
            </Button>
            <Button asChild variant="outline" className="border-2">
              <Link to="/get-involved">Get Involved</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
